import { Link } from "react-router-dom";
import { type FC, type ReactNode } from "react";

interface RunPipelineLinkProps {
  pipelineId: number;
  pipelineName: string;
  /** Undefined is treated as "pipeline", the same as every row created before this column
   *  existed on the payload. */
  subjectType?: "pipeline" | "process";
  /** The process this run was created from. Only set when subjectType is "process". Carried
   *  through to the DOM (data-source-process-id) rather than dropped: there is no route yet
   *  to open a process editor on its own (see the component doc comment), so nothing can link
   *  to it today, but the id is real data a future feature can use without another backend
   *  round trip. */
  sourceProcessId?: number | null;
  /** Class list for the anchor. Defaults to the runs list table cell treatment. */
  linkClassName?: string;
  /** Class list for the plain text a process subject run renders instead. Defaults to the
   *  runs list table cell treatment. */
  textClassName?: string;
  /** Opens the pipeline in a new tab, which is what the analytics table does for every link
   *  in its rows. */
  openInNewTab?: boolean;
  /** Replaces the rendered label, for a surface that wraps the name in something of its own.
   *  The dashboard passes a TruncatedText so its narrow column keeps truncating and keeps its
   *  tooltip. Rendered in BOTH branches: a process subject run must still get the caller's
   *  content, just not wrapped in a link. Defaults to plain pipelineName. */
  children?: ReactNode;
}

/**
 * Renders a run's pipeline reference. A run whose subject is a process must not link to
 * /pipelines/{pipelineId}: that id is the one node pipeline synthesized for a standalone
 * process run, and business/pipeline/pipeline.ts's assertNotProcessSubject guard (Task 4 of
 * the process git phase 2D-1 plan) refuses to open it for edit, delete or duplicate, so the
 * pipeline editor is a dead end for it.
 *
 * There is currently no route in this app that opens a process editor on its own; the only
 * place it renders is nested inside a pipeline's workflow graph
 * (workflow/components/create-edit-process-form). Rather than link somewhere that would 404,
 * a process subject run renders as plain text naming the synthesized pipeline's row (which
 * already carries the process's name, see standalone-run.ts's "(standalone)" suffix). See the
 * task 9 report for this finding.
 *
 * FOUR surfaces render this reference and all four must agree: the runs list, the run detail
 * header, the run analytics table, and the project dashboard's recent runs table. They differ
 * only in styling, in whether the link opens a new tab, and in what they render as the label,
 * which is why those are props rather than four copies of the rule.
 *
 * ⚠️ The dashboard was NOT one of them until 2026-08-29. It carried its own
 * `<Link to={`/pipelines/${pipelineId}`}>` with no subjectType check, so a standalone process
 * run in the ten most recent rows linked straight into the dead end this component exists to
 * prevent. The row already carried subjectType; the cell simply ignored it. If you add a fifth
 * surface, use this component. Nothing automated stops you duplicating the rule instead, which
 * is exactly how the dashboard drifted.
 */
const RunPipelineLink: FC<RunPipelineLinkProps> = ({
  pipelineId,
  pipelineName,
  subjectType,
  sourceProcessId,
  linkClassName = "via-link truncate",
  textClassName = "truncate",
  openInNewTab = false,
  children,
}) => {
  const label = children ?? pipelineName;

  if (subjectType === "process") {
    return (
      <span
        className={textClassName}
        data-source-process-id={sourceProcessId ?? undefined}
        title="Created directly from a process, not a pipeline">
        {label}
      </span>
    );
  }

  return (
    <Link
      to={`/pipelines/${pipelineId}`}
      className={linkClassName}
      {...(openInNewTab ? { target: "_blank", rel: "noreferrer" } : {})}>
      {label}
    </Link>
  );
};

export default RunPipelineLink;
